import { IQuestionCipher } from '@/contracts/modules/education/cipher/IQuestionCipher'
import { IQuestionData } from '@/contracts/modules/education/entities/IQuestionData'
import { EnumEducationError } from '@/contracts/modules/education/enum/EnumEducationError'
import { GCM_IV_BYTES, decryptGcm, encryptGcm } from '@/helpers/aesGcm'
import { base64UrlToBytes, bytesToBase64Url } from '@/helpers/base64url'

const KEY_ALGORITHM: AesKeyGenParams = { name: 'AES-GCM', length: 256 }
const KEY_USAGES: KeyUsage[] = ['encrypt', 'decrypt']
// тег AES-GCM — 16 байт: строка короче IV и тега не может быть шифротекстом
const TAG_BYTES = 16
/** Предел открытых данных вопроса (JSON {question, answer}) в байтах */
export const DATA_LIMIT_BYTES = 32 * 1024
/** AAD данных вопроса: шифротекст другого назначения не расшифруется как данные вопроса */
const AAD = new TextEncoder().encode('education:data')

function isQuestionData(value: unknown): value is IQuestionData {
  return typeof value === 'object' && value !== null
    && typeof (value as IQuestionData).question === 'string' && typeof (value as IQuestionData).answer === 'string'
}

/**
 * Каждое шифрование — новый случайный IV: одним ключом вопрос шифруется при каждой правке.
 * Строка — base64url от IV и шифротекста подряд.
 */
export class QuestionCipher implements IQuestionCipher {
  createKey(): Promise<CryptoKey> {
    return crypto.subtle.generateKey(KEY_ALGORITHM, true, KEY_USAGES)
  }

  async encrypt(key: CryptoKey, data: IQuestionData): Promise<string> {
    const bytes = new TextEncoder().encode(JSON.stringify({ question: data.question, answer: data.answer }))
    if (bytes.length > DATA_LIMIT_BYTES) {
      throw new Error(EnumEducationError.DATA_TOO_LARGE)
    }
    const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_BYTES))
    const cipher = await encryptGcm(key, iv, AAD, bytes)
    const packed = new Uint8Array(iv.length + cipher.length)
    packed.set(iv)
    packed.set(cipher, iv.length)

    return bytesToBase64Url(packed)
  }

  async decrypt(key: CryptoKey, cipher: string): Promise<IQuestionData> {
    let text: string
    try {
      const packed = base64UrlToBytes(cipher)
      if (packed.length < GCM_IV_BYTES + TAG_BYTES) {
        throw new Error(EnumEducationError.CANNOT_DECRYPT)
      }
      const bytes = await decryptGcm(key, packed.slice(0, GCM_IV_BYTES), AAD, packed.slice(GCM_IV_BYTES))
      text = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
    } catch {
      throw new Error(EnumEducationError.CANNOT_DECRYPT)
    }
    let data: unknown
    try {
      data = JSON.parse(text)
    } catch {
      throw new Error(EnumEducationError.CANNOT_DECRYPT)
    }
    if (!isQuestionData(data)) {
      throw new Error(EnumEducationError.CANNOT_DECRYPT)
    }

    return { question: data.question, answer: data.answer }
  }
}
